import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import type { User } from '@printtat/shared';
import { useNavigate } from 'react-router-dom';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '@/lib/authToken';
import { apiFetch } from '@/lib/apiFetch';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  checkAuth: () => Promise<User | null>;
  refreshUser: () => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

type FetchResult =
  | { kind: 'ok'; user: User }
  | { kind: 'unauthorized' }
  | { kind: 'network-error' };

// Retry on network/5xx errors so a brief backend restart doesn't look like a logout.
const RETRY_DELAYS_MS = [0, 500, 1000, 2000];

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// 'ok' → signed in. 'unauthenticated' (401/400) → genuinely no session.
// 'error' (429/5xx/network) → transient: the refresh cookie may be perfectly
// valid, so the caller must NOT treat it as a logout — otherwise a shared
// per-IP rate-limit (e.g. switching between the two SPAs) bounces a live
// session to the sign-in page.
type BootstrapResult = 'ok' | 'unauthenticated' | 'error';

async function bootstrapSession(): Promise<BootstrapResult> {
  try {
    const r = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (r.ok) {
      const body = (await r.json()) as { accessToken?: string };
      if (!body.accessToken) return 'error';
      setAccessToken(body.accessToken);
      return 'ok';
    }
    if (r.status === 429 || r.status >= 500) return 'error';
    return 'unauthenticated';
  } catch {
    return 'error';
  }
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    if (!getAccessToken()) {
      const result = await bootstrapSession();
      if (result === 'unauthenticated') {
        setUser(null);
        setLoading(false);
        setError(null);
        return null;
      }
      if (result === 'error') {
        // Transient (rate-limited or a server blip) — don't wipe an existing
        // session or present a logout. Surface a connectivity error; a reload
        // recovers once the limit/outage clears.
        setLoading(false);
        setError('Unable to reach the server. Please check your connection.');
        return null;
      }
    }

    const fetchUser = async (): Promise<FetchResult> => {
      try {
        const response = await apiFetch('/auth/user');
        if (response.status === 401) return { kind: 'unauthorized' };
        if (!response.ok) return { kind: 'network-error' };
        const userData = (await response.json()) as User;
        return { kind: 'ok', user: userData };
      } catch {
        return { kind: 'network-error' };
      }
    };

    for (const delay of RETRY_DELAYS_MS) {
      if (delay > 0) await new Promise((r) => setTimeout(r, delay));
      const result = await fetchUser();

      if (result.kind === 'unauthorized') {
        clearAccessToken();
        setUser(null);
        setLoading(false);
        setError(null);
        return null;
      }

      if (result.kind === 'ok') {
        setUser(result.user);
        setLoading(false);
        setError(null);
        return result.user;
      }
    }

    setError('Unable to reach the server. Please check your connection.');
    setUser(null);
    setLoading(false);
    return null;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore
    }
    clearAccessToken();
    setUser(null);
    navigate('/sign-in', { replace: true });
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const authChecked = useRef(false);
  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    error,
    checkAuth,
    refreshUser,
    logout,
    isAuthenticated: !!user,
  }), [user, loading, error, checkAuth, refreshUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
